import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AIChatCard from "@/components/health/AIChatCard";
import { AISettingsDialog } from "@/components/AISettingsDialog";
import { VoiceMode } from "@/components/VoiceMode";
import { Robot, Plus, X, GearSix, Waveform } from "@phosphor-icons/react";

const PERSONA_CHIPS = [["none", "🤖 Универсал"], ["coach", "💪 Тренер"], ["diet", "🥗 Диетолог"], ["psy", "🧠 Психолог"], ["fin", "💰 Финансист"], ["translator", "🌍 Переводчик"]];

export default function AIPage() {
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [voiceMode, setVoiceMode] = useState(false);
  const threadId = params.get("t") || null;

  const setPersona = async (p) => {
    const s = { ...settings, persona: p };
    setSettings(s);
    try { await api.put("/ai/settings", s); } catch {}
  };

  const loadThreads = () => api.get("/ai/threads").then((r) => setThreads(r.data.threads)).catch(() => {});
  useEffect(() => {
    loadThreads();
    api.get("/ai/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const newThread = async () => {
    try {
      const { data } = await api.post("/ai/threads");
      setThreads((t) => [{ id: data.id, title: data.title }, ...t]);
      setParams({ t: data.id });
    } catch { toast.error("Не удалось создать диалог"); }
  };

  useEffect(() => {
    if (!threadId && threads.length === 0) return;
    if (!threadId && threads.length > 0) setParams({ t: threads[0].id }, { replace: true });
  }, [threads, threadId]);

  useEffect(() => {
    if (!threadId && threads.length === 0) {
      api.get("/ai/threads").then((r) => {
        if (!r.data.threads.length) newThread();
      }).catch(() => {});
    }
  }, []);

  const delThread = async (id) => {
    setThreads((t) => t.filter((x) => x.id !== id));
    if (threadId === id) setParams({});
    try { await api.delete(`/ai/threads/${id}`); } catch {}
  };

  return (
    <div className="fade-in max-w-3xl" data-testid="ai-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1.5">
            <Robot size={13} weight="fill" className="text-[#0084ff]" /> Чат-бот с памятью о вас и ваших данных
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">AI</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setVoiceMode(true)} data-testid="ai-voice-mode-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-[#0084ff] to-[#00c9a7] hover:brightness-110 transition-all">
            <Waveform size={17} weight="bold" /> Голос
          </button>
          <button onClick={() => setSettingsOpen(true)} data-testid="ai-settings-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg glass-panel border border-white/[0.07] text-sm text-muted-foreground hover:text-foreground transition-colors">
            <GearSix size={17} /> Настройки
          </button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Фото, PDF, аудио и видео — прикрепляйте, бот прочитает и расшифрует. Говорите голосом, слушайте ответы.
        Скажите «запомни, что…» или «отвечай короче» — бот настроит себя сам.
      </p>

      {settings && (
        <div className="flex items-center gap-1.5 mb-3 overflow-x-auto pb-1" data-testid="ai-personas-row">
          {PERSONA_CHIPS.map(([id, label]) => (
            <button key={id} onClick={() => setPersona(id)} data-testid={`ai-persona-${id}`}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                settings.persona === id ? "bg-[#00c9a7]/20 text-[#00c9a7] ring-1 ring-[#00c9a7]/40" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-1.5 mb-4 overflow-x-auto pb-1" data-testid="ai-threads-row">
        <button onClick={newThread} data-testid="ai-thread-new"
          className="flex items-center gap-1 shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#0084ff] text-white hover:brightness-110 transition-all">
          <Plus size={13} weight="bold" /> Новый
        </button>
        {threads.map((t) => (
          <span key={t.id} data-testid={`ai-thread-chip-${t.id}`}
            className={`flex items-center gap-1 shrink-0 rounded-full pl-3 pr-1.5 py-1.5 text-xs font-semibold transition-colors ${
              threadId === t.id ? "bg-[#0084ff]/20 text-[#4dabf5] ring-1 ring-[#0084ff]/40" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            <button onClick={() => setParams({ t: t.id })} className="max-w-[140px] truncate">{t.title}</button>
            <button onClick={() => delThread(t.id)} data-testid={`ai-thread-delete-${t.id}`} className="p-0.5 hover:text-red-400"><X size={11} /></button>
          </span>
        ))}
      </div>

      {threadId && settings != null && (
        <AIChatCard key={threadId} voice attach tts autoVoice={settings.auto_voice} threadId={threadId}
          title={settings.model === "claude" ? "Claude Sonnet 5" : "GPT-5.4"}
          onMeta={(m) => { if (m.settings_saved) api.get("/ai/settings").then((r) => setSettings(r.data)).catch(() => {}); }} />
      )}

      <AISettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen}
        onSaved={(s) => setSettings(s)} />
      {voiceMode && <VoiceMode threadId={threadId} onClose={() => setVoiceMode(false)} />}
    </div>
  );
}
